import {
  SelectedVariants,
  ServicePaymentDto,
  PaymentType,
} from '@wix/bookings-uou-types';
import {
  BookingLineItem,
  ParticipantChoices,
  PriceInfo,
} from '@wix/ambassador-bookings-v2-price-info/types';
import type {
  ServiceChoice,
  ServiceOption,
  ServiceOptions,
  ServiceOptionsAndVariants,
  ServiceVariant,
  ServiceVariants,
  Money,
} from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { ServiceOptionType } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { PaymentDetails, CustomOption } from '../../types/dynamicPrice';
import { PaymentDtoMapper, PriceUtils } from '@wix/bookings-uou-mappers';
import { PriceChoice } from '../../components/BookingsForm/Widget/PriceOptionDropdownContainer/PriceOptionDropdown/PriceOptionDropdown';
import { toLocalDateTimeString } from '../time';

export const mapVariantsToLineItems = ({
  serviceId,
  resourceId,
  selectedVariants,
}: {
  serviceId: string;
  resourceId: string;
  selectedVariants: SelectedVariants[];
}): BookingLineItem[] => {
  const staffMemberId = selectedVariants
    .flatMap((variant) => variant.choices || [])
    .find((choice: ServiceChoice) => choice?.staffMemberId)?.staffMemberId;

  const serviceChoices = selectedVariants.map(
    ({ numberOfParticipants, choices }) => ({
      numberOfParticipants,
      choices: choices?.filter(isCustomChoice).map(({ id, optionId }) => ({
        choiceId: id,
        optionId,
      })),
    }),
  );

  return [
    {
      serviceId,
      resourceId: staffMemberId || resourceId,
      serviceChoices,
    },
  ];
};

export function isRuleBasedOption(options?: ServiceOptions): boolean {
  const type = options?.values?.[0]?.type;
  return (
    type === ServiceOptionType.DATE_TIME ||
    type === ServiceOptionType.NUMBER_OF_PARTICIPANTS
  );
}

export const getParticipantRangeVariant = (
  serviceOptionsAndVariants: ServiceOptionsAndVariants | undefined,
  numberOfParticipants: number,
): ServiceVariant | undefined =>
  serviceOptionsAndVariants?.variants?.values?.find((variant) => {
    const range = variant.choices?.[0]?.participantRange;
    if (!range) {
      return false;
    }
    const min = range.minParticipants ?? 1;
    const max = range.maxParticipants ?? Number.MAX_SAFE_INTEGER;
    return numberOfParticipants >= min && numberOfParticipants <= max;
  });

export const mapRuleBasedToLineItems = ({
  serviceId,
  resourceId,
  options,
  numberOfParticipants,
  slotStartDate,
  slotTimezone,
}: {
  serviceId: string;
  resourceId: string;
  options?: ServiceOptions;
  numberOfParticipants: number;
  slotStartDate?: string;
  slotTimezone?: string;
}): BookingLineItem[] => {
  const lineItem: BookingLineItem = {
    serviceId,
    resourceId,
    serviceChoices: [{ numberOfParticipants }],
  };

  if (
    options?.values?.[0]?.type === ServiceOptionType.DATE_TIME &&
    slotStartDate
  ) {
    lineItem.localStartDate = toLocalDateTimeString(
      slotStartDate,
      slotTimezone,
    );
    lineItem.timeZone = slotTimezone;
  }

  return [lineItem];
};

export const mapPriceInfoToSelectedVariants = (
  priceInfo: PriceInfo,
): SelectedVariants[] =>
  priceInfo.bookingLineItems?.[0]?.serviceChoices?.reduce<SelectedVariants[]>(
    (selectedVariants, serviceChoice) => {
      const choice = serviceChoice.choices?.[0];
      if (!choice?.dateTime && !choice?.participantRange) {
        return selectedVariants;
      }

      selectedVariants.push({
        numberOfParticipants: serviceChoice.numberOfParticipants ?? undefined,
        choices: [
          {
            ...choice,
            id: choice.choiceId ?? undefined,
          },
        ],
      });

      return selectedVariants;
    },
    [],
  ) || [];

export const mapPriceInfoToPaymentDetails = ({
  priceInfo,
  dynamicPriceOptions,
  locale,
}: {
  priceInfo: PriceInfo;
  dynamicPriceOptions: ServiceOptions;
  locale: string;
}): PaymentDetails[] => {
  const isCustom = isCustomTypeOption(dynamicPriceOptions);
  const currency = priceInfo.currency!;

  return (
    priceInfo.bookingLineItems?.[0]?.serviceChoices?.reduce<PaymentDetails[]>(
      (paymentDetails, serviceChoice) => {
        const choice = serviceChoice.choices?.[0];
        const choiceName = isCustom
          ? choice?.custom
          : choice?.dateTime?.name ?? choice?.participantRange?.name;

        if (!choiceName) {
          return paymentDetails;
        }

        paymentDetails.push({
          numberOfParticipants: serviceChoice.numberOfParticipants || 1,
          choice: choiceName,
          price: {
            value: String(serviceChoice.pricePerParticipant),
            currency,
            formattedValue: PriceUtils.getFormattedCurrency({
              price: serviceChoice.pricePerParticipant!,
              currency,
              locale,
            }),
          },
        });

        return paymentDetails;
      },
      [],
    ) || []
  );
};

function isCustomTypeOption(dynamicPriceOptions: ServiceOptions) {
  return (
    dynamicPriceOptions?.values?.length === 1 &&
    dynamicPriceOptions?.values?.[0].type === ServiceOptionType.CUSTOM
  );
}

function isCustomChoice(choice: ServiceChoice): boolean {
  return !!choice.custom || !!choice.duration;
}

function isRuleBasedChoice(choice: ServiceChoice): boolean {
  return !!choice.dateTime || !!choice.participantRange;
}

export function isDynamicVariant(variant: SelectedVariants): boolean {
  return !!variant.choices?.find((choice) => isCustomChoice(choice));
}

export function isRuleBasedVariant(variant: SelectedVariants): boolean {
  return !!variant.choices?.find((choice) => isRuleBasedChoice(choice));
}

export function mapDynamicParticipantsNumber(
  selectedVariants?: SelectedVariants[],
): {
  participantsChoices?: ParticipantChoices;
  totalParticipants?: number;
} {
  if (!selectedVariants?.length) {
    return {};
  }

  if (
    isDynamicVariant(selectedVariants[0]) ||
    isRuleBasedVariant(selectedVariants[0])
  ) {
    return {
      participantsChoices: {
        serviceChoices: selectedVariants.map(
          ({ numberOfParticipants, choices }) => ({
            numberOfParticipants,
            choices: choices?.map(({ id, optionId }) => ({
              choiceId: id,
              optionId,
            })),
          }),
        ),
      },
    };
  }

  return {
    totalParticipants: selectedVariants[0]?.numberOfParticipants || 0,
  };
}

function serviceChoiceToDropdownOptionMapper({
  choice,
  id,
  price,
  dateRegionalSettingsLocale,
}: {
  choice: string;
  id?: string;
  price?: Money;
  dateRegionalSettingsLocale?: string;
}): PriceChoice {
  let priceText = '';
  if (price && dateRegionalSettingsLocale) {
    const paymentDtoMapper = new PaymentDtoMapper(dateRegionalSettingsLocale);
    const priceValue = Number(price.value!);

    const servicePaymentDto: ServicePaymentDto = {
      currency: price!.currency!,
      price: priceValue,
      isFree: priceValue === 0,
      paymentType: PaymentType.ONLINE, // arbitrary, not used
      minCharge: 0,
      priceText: '',
    };
    priceText = paymentDtoMapper.priceText(servicePaymentDto);
  }
  return {
    title: choice,
    choiceId: choice,
    id,
    ...(priceText ? { price: priceText } : {}),
  };
}

function mapSelectedVariantsToCustomOptions(
  customOptions: CustomOption[],
  selectedVariants: SelectedVariants[],
): CustomOption[] {
  const prepopulatedCustomOptions: CustomOption[] = customOptions;
  selectedVariants.forEach((variant) => {
    if (isDynamicVariant(variant)) {
      const optionId = variant.choices?.[0]?.optionId!;
      const selectedVariant = customOptions.find(
        (option) => option.optionId === optionId,
      );
      const custom: string = variant.choices?.[0]?.custom!;
      const userChoice = selectedVariant?.options.find(
        (choice) => choice.choiceId === custom,
      );
      if (!userChoice) {
        return;
      }
      userChoice!.numberOfParticipantsInitialValue =
        variant.numberOfParticipants;
    }
  });
  return prepopulatedCustomOptions;
}

function customServiceOptionsToCustomOptions({
  serviceOptions,
  serviceVariants,
  dateRegionalSettingsLocale,
}: {
  serviceOptions: ServiceOptions;
  serviceVariants?: ServiceVariants;
  dateRegionalSettingsLocale: string;
}): CustomOption[] {
  const customOptions: CustomOption[] = [];
  serviceOptions?.values
    ?.filter(
      (serviceOption: ServiceOption) =>
        serviceOption.type === ServiceOptionType.CUSTOM,
    )
    .forEach((serviceOption: ServiceOption) => {
      const filteredVariants = serviceVariants?.values?.filter(
        (variant: ServiceVariant) =>
          variant.choices?.[0].optionId === serviceOption.id &&
          !!variant.choices?.[0].custom,
      );
      customOptions.push({
        optionId: serviceOption.id!,
        name: serviceOption.customData?.name!,
        options:
          filteredVariants?.map((variant) =>
            serviceChoiceToDropdownOptionMapper({
              choice: variant.choices?.[0].custom!,
              id: variant.choices?.[0].id || undefined,
              price: variant.price!,
              dateRegionalSettingsLocale,
            }),
          ) || [],
      });
    });
  return customOptions;
}

export function selectedVariantsAndServiceOptionsToCustomOptions({
  selectedVariants = [],
  serviceOptions = { values: [] },
  serviceVariants = { values: [] },
  dateRegionalSettingsLocale = 'en-US',
}: {
  selectedVariants?: SelectedVariants[];
  serviceOptions?: ServiceOptions;
  serviceVariants?: ServiceVariants;
  dateRegionalSettingsLocale?: string;
}): CustomOption[] {
  let customOptions = customServiceOptionsToCustomOptions({
    serviceOptions,
    serviceVariants,
    dateRegionalSettingsLocale,
  });

  if (selectedVariants.length) {
    customOptions = mapSelectedVariantsToCustomOptions(
      customOptions,
      selectedVariants,
    );
  }

  return customOptions;
}

export function isPreferenceOption(dynamicPriceOptions: ServiceOptions) {
  return (
    dynamicPriceOptions?.values?.length === 1 &&
    dynamicPriceOptions?.values?.[0].type !== ServiceOptionType.CUSTOM
  );
}
